import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { cn } from '@/lib/utils'
import { Button } from '@components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { Upload, X, Send } from 'lucide-react'
import { INPUT_OPTIONS, aiBtnList } from 'utils/constants'
import { toggleAuthModal } from 'store/actions/header.action'
import { addSearchQuery } from 'store/actions/chat.action'
import { PromptExamples } from './PromptExamples'
import AnimatedTextarea from './AnimatedTextArea'

const GlowingInput = ({ initialInputType = 'styley-text-to-image' }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const uploadRef = useRef(null)
  const textareaRef = useRef(null)
  const [inputValue, setInputValue] = useState('')
  const [activeInputType, setActiveInputType] = useState(initialInputType)
  const [selectedTab, setSelectedTab] = useState('image')
  const [files, setFiles] = useState([])

  const { activeTheme } = useSelector((state) => state.theme)
  const { user } = useSelector((state) => state.auth)

  const isWhiteTheme = !activeTheme?.includes('sharp-theme')
  const isDarkTheme = !isWhiteTheme
  const isSharpTheme = activeTheme?.includes('sharp-theme')

  // Sync the selected tab with the initial input type
  useEffect(() => {
    const option = INPUT_OPTIONS.find((opt) => opt.inputType === initialInputType)
    if (option) {
      setSelectedTab(option.value)
    }
  }, [initialInputType])

  // Handle tab change and update the URL query
  const handleTabChange = useCallback(
    (tabValue) => {
      setSelectedTab(tabValue)

      const option = INPUT_OPTIONS.find((opt) => opt.value === tabValue)
      if (option) {
        setActiveInputType(option.inputType)

        router.push(
          {
            pathname: '/',
            query: {
              ...router.query,
              inputType: option.inputType,
            },
          },
          undefined,
          { shallow: true }
        )
      }
    },
    [router]
  )

  // Handle form submission
  const handleSubmit = useCallback(
    (e) => {
      e?.preventDefault()
      if (!inputValue.trim()) return

      const selected = activeInputType

      if (!aiBtnList[selected]?.ai) {
        if (!user) {
          dispatch(toggleAuthModal('sign_in'))
          return
        }

        router.push(
          {
            pathname: '/',
            query: {
              ...router.query,
              modalType: 'editor',
              input: inputValue,
            },
          },
          undefined,
          { shallow: true }
        )
      } else {
        dispatch(addSearchQuery({ prompt: inputValue, model: selected }))
        router.push('/chat')
      }
    },
    [inputValue, router, activeInputType, user, dispatch]
  )

  // Handle textarea input change and auto-resize
  const handleTextareaChange = useCallback((e) => {
    const textarea = e.target
    setInputValue(textarea.value)
    // Reset height to measure actual content height
    textarea.style.height = '50px'

    // Only expand if content actually overflows the initial height
    if (textarea.scrollHeight > 50) {
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  // Handle Enter key press for submission
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit(e)
      }
    },
    [handleSubmit]
  )

  // Handle file uploads
  const handleUpload = useCallback((e) => {
    const newFiles = []
    const filesArray = Array.from(e.target.files)

    const filePromises = filesArray.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            newFiles.push({
              name: file.name,
              src: reader.result,
            })
            resolve()
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
    )

    Promise.all(filePromises)
      .then(() => {
        setFiles((prevFiles) => [...prevFiles, ...newFiles])
      })
      .catch((err) => {
        console.error('Error reading files:', err)
      })

    e.target.value = '' // Reset input value after upload
  }, [])

  // Remove a file from the list
  const handleRemoveFile = useCallback((index) => {
    setFiles((prevFiles) => {
      const tempFiles = [...prevFiles]
      tempFiles.splice(index, 1)
      return tempFiles
    })
  }, [])

  // Update textarea value and adjust height
  const setTextareaValue = useCallback((text) => {
    setInputValue(text)
    if (textareaRef.current) {
      // Reset to initial height first
      textareaRef.current.style.height = '50px'

      // Only expand if needed
      if (textareaRef.current.scrollHeight > 50) {
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
      }
    }
  }, [])

  return (
    <>
      <div className="relative group max-w-5xl 2xl:max-w-6xl mx-auto px-4 sm:px-6">
        <form
          onSubmit={handleSubmit}
          className={cn(
            'w-full mx-auto shadow-2xl p-4 sm:p-6 mb-10 xl:mb-6 bg-white backdrop-blur-sm border border-white/80 relative z-10',
            isSharpTheme ? 'rounded-none' : 'rounded-2xl sm:rounded-3xl',
            isDarkTheme && 'backdrop-blur-xl bg-white/10 border-none'
          )}
        >
          <div className="relative bg-black  overflow-hidden">
            {/* Gradient border with low z-index to avoid blocking interactions */}
            <div className="absolute inset-0 border-gradient z-0" />

            {/* Textarea section */}
            <AnimatedTextarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onSubmit={handleSubmit}
            />

            {/* Uploaded files section */}
            {files.length > 0 && (
              <div className="px-4 sm:px-6 pb-2 z-10">
                <div className="flex flex-wrap gap-2">
                  {files.map((item, i) => (
                    <div
                      key={`file-${i}`}
                      className={cn(
                        'relative text-xs cursor-pointer py-1.5 pl-2 pr-6 flex items-center bg-gray-800 text-white',
                        isSharpTheme ? 'rounded-none' : 'rounded-lg'
                      )}
                      onClick={() => handleRemoveFile(i)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleRemoveFile(i)
                        }
                      }}
                      aria-label={`Remove file ${item.name}`}
                    >
                      <span className="truncate max-w-[120px] sm:max-w-[150px]">
                        {item.name.length > 20 ? `${item.name.substring(0, 20)}...` : item.name}
                      </span>
                      <X className="absolute right-1 w-3 h-3" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabs section */}
            <div className="border-t border-gray-800">
              <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
                <div className="py-3 px-4 sm:px-6 z-10">
                  <TabsList
                    className={cn(
                      'flex flex-wrap !justify-start !h-auto gap-1.5 sm:gap-2 p-1 sm:p-1.5 bg-gray-900 relative w-full',
                      isSharpTheme ? 'rounded-none' : 'rounded-md'
                    )}
                  >
                    {INPUT_OPTIONS.map((option) => (
                      <TabsTrigger
                        key={option.value}
                        value={option.value}
                        onClick={() => handleTabChange(option.value)}
                        className={cn(
                          'text-xs py-1.5 sm:py-2 px-2 sm:px-3 transition-all data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-sm min-w-max flex-shrink-0 z-10',
                          isSharpTheme ? 'rounded-none' : 'rounded-sm'
                        )}
                        aria-label={`Switch to ${option.label} mode`}
                      >
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <span className="text-xs sm:text-sm">{option.icon}</span>
                          <span className="text-xs sm:text-sm whitespace-nowrap">{option.label}</span>
                        </div>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                {INPUT_OPTIONS.map((option) => (
                  <TabsContent key={option.value} value={option.value} />
                ))}
              </Tabs>
            </div>

            {/* Action buttons */}
            <div className="flex px-4 sm:px-6 pt-3 sm:pt-4 pb-4 sm:pb-5 items-center border-t border-gray-800 justify-between gap-3 z-10">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 h-10 sm:gap-2 z-10 bg-white border-gray-200 text-black hover:bg-gray-100 hover:text-black px-3 sm:px-4 py-2 text-xs sm:text-sm"
                onClick={() => uploadRef.current?.click()}
                aria-label="Attach files"
              >
                <Upload className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="whitespace-nowrap">Attach</span>
                <input
                  ref={uploadRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                  aria-label="Upload image files"
                />
              </Button>
              <Button
                type="submit"
                size="sm"
                className={cn(
                  'gap-1.5 sm:gap-2 h-10 z-10 bg-gradient-to-r from-[#E61D2B] to-[#8F00FF] hover:from-[#D41B28] hover:to-[#7E00E6] text-white px-4 sm:px-6 py-2 text-xs sm:text-sm flex-shrink-0',
                  isSharpTheme && 'rounded-none'
                )}
                aria-label="Generate content"
              >
                <Send className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="whitespace-nowrap">Generate</span>
              </Button>
            </div>
          </div>
        </form>
      </div>

      <PromptExamples setTextareaValue={setTextareaValue} setActiveTab={handleTabChange} />
    </>
  )
}

export default GlowingInput
